import { z } from 'zod'

import {
  messageRecordSchema,
  transcriptRecordSchema,
  turnCompletedRecordSchema,
  turnRevertedRecordSchema,
} from './transcriptRecord.ts'
import { viewPageTargetSchema } from './viewPage.ts'

// storage と runtime の応答の形の正本は、salescore-agent-storage と salescore-agent-runtime の DESIGN.md の「インターフェース」にある。
// ここではそれを、BE と画面が検証できる形で書き写す

// POST /api/agent/gateway/sessions/{agentSessionId}/turns の応答で、BE が振った turnId を返すヘッダー。
// 応答の本文は runtime の SSE をそのまま流すので、中断（cancel）に使う turnId はヘッダーで受け取る
export const TURN_ID_HEADER = 'x-turn-id'

// ツールを実行する前に利用者の承認を求めるか。runtime の approvalPolicy と同じ値
const approvalPolicySchema = z.enum(['never', 'on-request', 'untrusted'])

export type ApprovalPolicy = z.infer<typeof approvalPolicySchema>

// POST /api/agent/gateway/sessions/{agentSessionId}/turns の本文
export const runTurnBodySchema = z.object({
  // 利用者の指示。前後の空白を除いて空なら断る
  prompt: z.string().trim().min(1),
  // ツールを実行する前に利用者の承認を求めるか。省くと runtime の既定（CODEX_APPROVAL_POLICY）を使う
  approvalPolicy: approvalPolicySchema.optional(),
  // アップロード済みの添付ファイルのパス。形と、ファイルがあるかは runtime が確かめる
  attachments: z.array(z.string()).optional(),
  // 会話の対象の view。画面は viewId だけを送り、種別と名前は BE が組織の view を引き直して埋める。省くか null なら対象なし
  page: viewPageTargetSchema.nullish(),
})

// GET /api/agent/gateway/sessions/{agentSessionId}/turns のクエリ。件数の範囲は storage の getTurns が確かめる
export const listTurnsQuerySchema = z.object({
  // 1ページのターンの数。省くと storage の既定（10ターン）
  limit: z.coerce.number().int().optional(),
  // 前のページの応答の nextBefore。省くと最新のターンから
  before: z.string().optional(),
})

/**
 * storage と runtime が後から足した値を受け取り、unknown に置き換えるスキーマ（ADR-006）。
 * 知っている値（known）は受け付けない。知っている値で形が崩れた応答を、後から足した値として通さないためである。
 * unknown に置き換えるのは、画面が status === 'completed' などで型を絞り込めるようにするためである。
 * 知っている値で断るときは検証を打ち切る（abort）。打ち切らないと、共用体の検証の失敗が、知っている値の形の誤りでなく、この確認の失敗として報告される
 */
const unknownValueSchema = (known: readonly string[]): z.ZodPipe<z.ZodString, z.ZodTransform<'unknown', string>> =>
  z
    .string()
    .refine((value) => !known.includes(value), { abort: true })
    .transform(() => 'unknown' as const)

// 会話履歴の1ターンのうち、状態によらず持つ項目。salescore-agent-storage の DESIGN.md の「getTurns のターン」と同じ形
const agentTurnBase = {
  turnId: z.string(),
  // ターンを受け付けた日時（ISO 8601）
  createdAt: z.string(),
  // 指示と最終回答。会話記録のレコードをそのまま返す
  messages: z.array(messageRecordSchema),
}

// storage が会話記録から決めるターンの状態。終了イベントがなければ running、巻き戻しイベントがあれば終了イベントより優先して reverted
const AGENT_TURN_STATUSES = ['running', 'completed', 'interrupted', 'failed', 'reverted'] as const

// 会話履歴の1ターン。状態ごとに、終えた日時（ISO 8601）、終了イベント、巻き戻しイベントを持つかが決まる。
// storage は、終了イベントの completedAt が文字列でなければ、終えたターンでも completedAt を null にする
const agentTurnSchema = z.union([
  z.discriminatedUnion('status', [
    z.object({
      ...agentTurnBase,
      status: z.literal('running'),
      completedAt: z.null(),
      completion: z.null(),
      reversion: z.null(),
    }),
    z.object({
      ...agentTurnBase,
      status: z.literal('completed'),
      completedAt: z.string().nullable(),
      completion: turnCompletedRecordSchema.extend({ status: z.literal('completed') }),
      reversion: z.null(),
    }),
    z.object({
      ...agentTurnBase,
      status: z.literal('interrupted'),
      completedAt: z.string().nullable(),
      completion: turnCompletedRecordSchema.extend({ status: z.literal('interrupted') }),
      reversion: z.null(),
    }),
    // storage は、ランナーが知らない状態を書いた終了イベントも、成功と見せないよう failed として返す。
    // そのため、終了イベントの状態は文字列かだけを確かめる
    z.object({
      ...agentTurnBase,
      status: z.literal('failed'),
      completedAt: z.string().nullable(),
      completion: turnCompletedRecordSchema.extend({ status: z.string() }),
      reversion: z.null(),
    }),
    // 終了イベントを書く前に巻き戻すこともあるので、終了イベントはないこともある。
    // 終了イベントの状態は巻き戻す前のものなので、文字列かだけを確かめる
    z.object({
      ...agentTurnBase,
      status: z.literal('reverted'),
      completedAt: z.string().nullable(),
      completion: turnCompletedRecordSchema.extend({ status: z.string() }).nullable(),
      reversion: turnRevertedRecordSchema,
    }),
  ]),
  // storage が後から足した状態のターン。状態は unknown に置き換え、画面は「不明」として表示する（ADR-006）
  z.object({
    ...agentTurnBase,
    status: unknownValueSchema(AGENT_TURN_STATUSES),
    completedAt: z.string().nullable(),
    completion: transcriptRecordSchema.nullable(),
    reversion: transcriptRecordSchema.nullable(),
  }),
])

// GET /api/agent/gateway/sessions/{agentSessionId}/turns の応答。ターンは新しい順に並ぶ
export const listTurnsResponseSchema = z.object({
  turns: z.array(agentTurnSchema),
  // より古いターンを読むときの before。null なら最初のターンまで読んだ
  nextBefore: z.string().nullable(),
})

export type ListTurnsResponse = z.infer<typeof listTurnsResponseSchema>

// GET /api/agent/gateway/sessions/{agentSessionId}/turns/{turnId}/events と .../stream のクエリ
export const turnOffsetQuerySchema = z.object({
  // transcript.jsonl を読み始めるバイト位置。省くと先頭から
  offset: z.coerce.number().int().optional(),
})

// GET /api/agent/gateway/sessions/{agentSessionId}/turns/{turnId}/events の応答。storage の getTurnEvents の応答から success を除いたもの
export const getTurnEventsResponseSchema = z.object({
  // 改行で終わる行から読んだレコード
  records: z.array(transcriptRecordSchema),
  // 続きを読むときに offset に渡す値
  nextOffset: z.number(),
  // 読み取りがファイルの末尾に届いたか
  reachedEnd: z.boolean(),
})

export type GetTurnEventsResponse = z.infer<typeof getTurnEventsResponseSchema>

// POST /api/agent/gateway/sessions/{agentSessionId}/turns/{turnId}/cancel の応答。runtime の cancel の応答と同じ形。
// runtimeLost は、実行環境ごと失われたターンを、中断として確定したこと
// runtime が後から足した理由は unknown に置き換え、画面は「不明」として表示する（ADR-006）
const CANCEL_ACCEPTED_REASONS = ['runtimeLost'] as const
const CANCEL_REJECTED_REASONS = ['alreadyCompleted', 'notFound'] as const
const unknownCancelReasonSchema = unknownValueSchema([...CANCEL_ACCEPTED_REASONS, ...CANCEL_REJECTED_REASONS])

export const cancelTurnResponseSchema = z.discriminatedUnion('accepted', [
  z.object({
    accepted: z.literal(true),
    reason: z.union([z.enum(CANCEL_ACCEPTED_REASONS), unknownCancelReasonSchema]).optional(),
  }),
  z.object({
    accepted: z.literal(false),
    reason: z.union([z.enum(CANCEL_REJECTED_REASONS), unknownCancelReasonSchema]),
  }),
])

export type CancelTurnResponse = z.infer<typeof cancelTurnResponseSchema>

// POST /api/agent/gateway/sessions/{agentSessionId}/turns/{turnId}/approvals/{approvalId} の本文
export const answerApprovalBodySchema = z.object({
  decision: z.enum(['accept', 'decline']),
})

export type ApprovalDecision = z.infer<typeof answerApprovalBodySchema>['decision']

// POST /api/agent/gateway/sessions/{agentSessionId}/turns/{turnId}/approvals/{approvalId} の応答。runtime の approve の応答と同じ形。
// notRunning は runtime がそのターンを実行していない、notPending は期限切れか回答済み
// runtime が後から足した理由は unknown に置き換え、画面は「不明」として表示する（ADR-006）
const APPROVAL_REJECTED_REASONS = ['notRunning', 'notPending'] as const

export const answerApprovalResponseSchema = z.discriminatedUnion('accepted', [
  z.object({ accepted: z.literal(true) }),
  z.object({
    accepted: z.literal(false),
    reason: z.union([z.enum(APPROVAL_REJECTED_REASONS), unknownValueSchema(APPROVAL_REJECTED_REASONS)]),
  }),
])

export type AnswerApprovalResponse = z.infer<typeof answerApprovalResponseSchema>
